<template>
    <vx-modal v-model:visible="visible" ref="modalRef" size="md" padding="standard">
        <template v-slot:header> Two-Factor Authentication </template>

        <vx-alert color="success" :closeable="false">
            In order to use two-factor authentication, you must install the Google Authenticator or any other compatible
            application on your smartphone.
        </vx-alert>
        <template v-if="qrImage">
            <div class="d-flex justify-content-center">
                <div data-test="svg-qr-code" v-html="qrImage"></div>
            </div>
        </template>

        <vx-alert color="info" :closeable="false">
            If you lose your two-factor authentication device, you may use this emergency reset token to disable
            two-factor authentication on your account.
            <strong>This is the only time this token will be displayed, so be sure not to lose it!</strong>
        </vx-alert>

        <vx-popover hover arrow placement="top">
            <div
                @click.prevent="copyToken()"
                data-test="copy-token"
                class="font-mono break-all cursor-pointer p-4 bg-white rounded-sm mt-4"
            >
                {{ props.twoFactorResetCode }} <font-awesome-icon widthAuto :icon="faCopy"></font-awesome-icon>
            </div>
            <template v-slot:content> Click to Copy </template>
        </vx-popover>

        <div>
            <p class="mt-6 mb-2">Fill in the code from the authenticator app to enable two-factor authentication</p>
            <code-input v-model="token" :error="displayFirstError(validate.token)"></code-input>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click.prevent="visible = false"
                    tabindex="1"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Close
                </vx-button>
                <vx-button data-test="submit-btn" @click.prevent="enable" type="button" size="lg" block color="primary">
                    Enable TFA and Download Reset Token
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import { faCopy } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxButton, VxModal, VxPopover } from '@voxie/frontend-components';
import { useCopyText } from '~/composables/useCopyText';
import { ref, watch } from 'vue';
import axios from '~/services/axios';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { displayFirstError, validateRules } from '~/utils/validation';
import useVuelidate from '@vuelidate/core';
import { helpers, numeric, required } from '@vuelidate/validators';
import CodeInput from '~/components/general/CodeInput.vue';
import { download } from '~/utils/files';

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});

const props = defineProps({
    twoFactorResetCode: {
        type: String,
        default: null,
    },
    qrImage: {
        type: String,
        default: null,
    },
});

const token = ref('');
const loading = ref(false);
const modalRef = ref();

const { $externalResults, catchError } = useFormRequestHandler();

const validate = useVuelidate(
    {
        token: {
            required,
            numeric,
            length: helpers.withMessage('The code must be 6 characters length', (value) => 6 === value?.length),
        },
    },
    { token },
    {
        $externalResults,
    }
);

const { copyText } = useCopyText();

const copyToken = () => {
    copyText(props.twoFactorResetCode);
};

watch(visible, () => {
    if (!visible.value) {
        token.value = '';
        validate.value.$reset();
    }
});

const enable = async () => {
    if (loading.value) {
        return;
    }

    if (!(await validateRules(validate))) {
        return;
    }

    try {
        loading.value = true;
        await axios.post('/settings/two-factor-auth', { token: token.value });

        // Create and download txt file with reset token
        download(props.twoFactorResetCode, 'emergency_reset_token_Voxie');
        Bus.$emit('updateUser');

        visible.value = false;
        modalRef.value.scrollTo();
    } catch (e) {
        catchError(e);
    } finally {
        loading.value = false;
    }
};
</script>
